import React from 'react';
import './BlogHome.css';

const focusAreas = [
  'Linux',
  'Automation',
  'Observability',
  'DevOps',
  'Cloud-native infrastructure',
  'Platform engineering',
];

const techStack = [
  'Ansible',
  'Docker',
  'Docker Compose',
  'Prometheus',
  'Grafana',
  'GitHub Actions',
  'Nginx',
  'Bash',
];

const experiments = [
  'Automation workflows for repeatable infrastructure tasks',
  'Monitoring setup with Prometheus and Grafana',
  'Homelab infrastructure documentation',
  'Docker Compose service templates',
  'Ansible command output and playbook examples',
];

const goals = [
  'Learn Kubernetes fundamentals',
  'Build Terraform-based infrastructure examples',
  'Improve platform engineering skills',
  'Create reusable infrastructure templates',
  'Document systems clearly in professional English',
];

const sections = [
  { href: '#about', label: 'About the lab' },
  { href: '#focus', label: 'Focus areas' },
  { href: '#stack', label: 'Tech stack' },
  { href: '#experiments', label: 'Current experiments' },
  { href: '#screenshots', label: 'Screenshots' },
  { href: '#goals', label: 'Goals' },
];

function PillList({ items }: { items: string[] }) {
  return (
    <div className="pill-list">
      {items.map((item) => (
        <span className="pill" key={item}>
          {item}
        </span>
      ))}
    </div>
  );
}

function BlogHome() {
  return (
    <main className="blog-page">
      <header className="site-header">
        <a className="site-logo" href="/">
          KaliJerry Lab
        </a>
        <nav className="top-nav" aria-label="Primary navigation">
          <a href="#about">About</a>
          <a href="#experiments">Experiments</a>
          <a href="#goals">Goals</a>
        </nav>
      </header>

      <div className="page-shell">
        <article className="article">
          <p className="breadcrumb">Home / DevOps Lab / Infrastructure Notes</p>

          <header className="article-header">
            <p className="eyebrow">Infrastructure / Automation / Observability</p>
            <h1>kalijerry-lab: Personal Infrastructure and Automation Lab</h1>
            <p className="dek">
              A compact engineering portfolio for Linux, DevOps, monitoring,
              automation, and cloud-native infrastructure experiments.
            </p>
            <div className="article-meta">
              <span>By KaliJerry</span>
              <span>Updated May 2026</span>
              <span>4 min read</span>
            </div>
          </header>

          <section className="hero-card" aria-label="Lab summary">
            <div>
              <span className="card-kicker">Project focus</span>
              <h2>Practical systems, documented clearly.</h2>
              <p>
                This repository is where I document infrastructure experiments,
                reusable automation patterns, deployment notes, and operational
                learning.
              </p>
            </div>
            <pre>
              <code>{`$ lab status
scope: linux automation observability
stack: ansible docker prometheus grafana
output: notes scripts deployment patterns`}</code>
            </pre>
          </section>

          <div className="toc-mobile">
            <p>On this page</p>
            <div>
              {sections.map((section) => (
                <a href={section.href} key={section.href}>
                  {section.label}
                </a>
              ))}
            </div>
          </div>

          <section className="article-section" id="about">
            <h2>About the lab</h2>
            <p>
              I am building a practical engineering portfolio around
              infrastructure, automation, monitoring, and platform operations.
              The goal is to keep the work understandable: what was built, why
              it matters, how it runs, and what can be reused.
            </p>
            <p>
              Instead of making this website a complex content platform, the
              page works like a focused technical note. It highlights the core
              project and keeps the supporting sections short.
            </p>
          </section>

          <section className="article-section" id="focus">
            <h2>Focus areas</h2>
            <p>
              The lab is centered on infrastructure skills that are useful for
              day-to-day platform and operations work.
            </p>
            <PillList items={focusAreas} />
          </section>

          <section className="article-section split-section" id="stack">
            <div>
              <h2>Tech stack</h2>
              <p>
                These are the tools currently used across the lab notes,
                examples, and experiments.
              </p>
            </div>
            <PillList items={techStack} />
          </section>

          <section className="article-section" id="experiments">
            <h2>Current experiments</h2>
            <p>
              The current work is intentionally practical and documentation
              first. Each item should become a reusable note, command example,
              or deployment pattern.
            </p>
            <ol className="number-list">
              {experiments.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </section>

          <aside className="note-box">
            <strong>Note</strong>
            <p>
              The original README references screenshot files, but those files
              are not present in the local `kalijerry-lab` repository yet. The
              website keeps placeholders ready without rendering broken images.
            </p>
          </aside>

          <section className="article-section" id="screenshots">
            <h2>Screenshot placeholders</h2>
            <div className="preview-grid">
              <div>
                <span>Grafana dashboard preview</span>
              </div>
              <div>
                <span>Terminal automation preview</span>
              </div>
              <div>
                <span>Docker Compose preview</span>
              </div>
            </div>
          </section>

          <section className="article-section" id="goals">
            <h2>Goals</h2>
            <p>
              The next step is to make the lab more complete while keeping the
              content concise and useful.
            </p>
            <ul className="check-list">
              {goals.map((goal) => (
                <li key={goal}>{goal}</li>
              ))}
            </ul>
          </section>
        </article>

        <aside className="sidebar" aria-label="Article sidebar">
          <div className="sidebar-card">
            <p className="sidebar-title">On this page</p>
            <nav className="toc">
              {sections.map((section) => (
                <a href={section.href} key={section.href}>
                  {section.label}
                </a>
              ))}
            </nav>
          </div>

          <div className="sidebar-card compact">
            <p className="sidebar-title">Project profile</p>
            <dl>
              <div>
                <dt>Type</dt>
                <dd>Infrastructure lab</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>Active documentation</dd>
              </div>
              <div>
                <dt>Audience</dt>
                <dd>DevOps portfolio</dd>
              </div>
            </dl>
          </div>
        </aside>
      </div>
    </main>
  );
}

export default BlogHome;
